import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Checkbox,
  Grid,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Preview as PreviewIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { collection, getDocs, deleteDoc, doc, query, orderBy, where, updateDoc, Timestamp } from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import { validateDatabase } from "../../../../Utils/databaseGuard";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    LineElement,
    PointElement,
    LineController,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    Filler,
} from 'chart.js';

ChartJS.register(LineElement, PointElement, LineController, CategoryScale, LinearScale, Title, ChartTooltip, Legend, Filler);

const YearlyGraphAnalysis = () => {
  const [yearlyData, setYearlyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filterEmail, setFilterEmail] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemsToDelete, setItemsToDelete] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  useEffect(() => {
    loadYearlyData();
  }, []);

  const loadYearlyData = async () => {
    // CRITICAL: Validate database before any operations
    validateDatabase();
    
    setLoading(true);
    try {
      // Load yearly graph data
      const q = query(collection(DATABASE, "yearly_graph"), orderBy("created_at", "desc"));
      const querySnapshot = await getDocs(q);
      
      console.log(`Backend: Found ${querySnapshot.docs.length} documents`);
      
      const yearlyData = querySnapshot.docs.map((doc) => {
        const docData = doc.data();
        return {
          id: doc.id,
          ...docData,
          created_at: docData.created_at?.toDate?.() || new Date(),
        };
      });

      // Filter out corrupted entries (entries with undefined values)
      const validData = yearlyData.filter(item => 
        item.month !== undefined && 
        item.year !== undefined && 
        item.amount !== undefined
      );

      console.log(`Backend: ${validData.length} valid entries after filtering`);

      // Load users data to get email information
      const usersSnapshot = await getDocs(collection(DATABASE, "users"));
      const usersMap = new Map();
      usersSnapshot.docs.forEach((doc) => {
        const userData = doc.data();
        usersMap.set(doc.id, userData);
        // Also map by doc_id if it exists
        if (userData.doc_id) {
          usersMap.set(userData.doc_id, userData);
        }
      });

      // Enrich yearly graph data with actual user information
      const enrichedData = validData.map((item) => {
        const userData = usersMap.get(item.userID);
        return {
          ...item,
          userID: item.userID || "Unknown User",
          userEmail: userData?.email || item.userID || "Unknown User",
          userName: userData?.full_name || item.userID || "Unknown User",
        };
      });

      console.log(`Frontend: Setting ${enrichedData.length} enriched entries`);
      setYearlyData(enrichedData);
      findDuplicates(enrichedData);
    } catch (error) {
      console.error("Error loading yearly data:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load yearly graph data. This may be due to insufficient permissions.",
      });
    } finally {
      setLoading(false);
    }
  };

  const findDuplicates = (data) => {
    const duplicateMap = new Map();
    
    data.forEach((item) => {
      const key = `${item.userID}-${item.month}-${item.year}`;
      if (!duplicateMap.has(key)) {
        duplicateMap.set(key, []);
      }
      duplicateMap.get(key).push(item);
    });

    const duplicates = Array.from(duplicateMap.values()).filter(group => group.length > 1);
    console.log(`Duplicate detection: Found ${duplicates.length} duplicate groups`);
    setDuplicates(duplicates);
  };

  const filteredData = yearlyData.filter((item) => {
    const emailMatch = !filterEmail || item.userID?.toLowerCase().includes(filterEmail.toLowerCase());
    const monthMatch = !filterMonth || item.month?.toLowerCase().includes(filterMonth.toLowerCase());
    return emailMatch && monthMatch;
  });

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = filteredData.map((item) => item.id);
      setSelectedItems(newSelected);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleDeleteSelected = () => {
    if (selectedItems.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Items Selected",
        text: "Please select items to delete",
      });
      return;
    }

    setItemsToDelete(selectedItems);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const deletePromises = itemsToDelete.map((itemId) =>
        deleteDoc(doc(DATABASE, "yearly_graph", itemId))
      );

      await Promise.all(deletePromises);

      Swal.fire({
        icon: "success",
        title: "Success",
        text: `${itemsToDelete.length} items deleted successfully`,
      });

      setSelectedItems([]);
      setDeleteDialogOpen(false);
      loadYearlyData();
    } catch (error) {
      console.error("Error deleting items:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete selected items",
      });
    }
  };

  const handlePreviewYearlyGraph = () => {
    setPreviewDialogOpen(true);
  };

  const generateChartData = () => {
    if (!Array.isArray(yearlyData) || yearlyData.length === 0) {
      return null;
    }

    // Debug: Log the raw data to understand what we're working with
    console.log("Raw yearly data:", yearlyData);
    console.log("First few items:", yearlyData.slice(0, 3));

    // Sort data by created_at descending (like Flutter app query)
    const sortedData = [...yearlyData].sort((a, b) => {
      return new Date(b.created_at) - new Date(a.created_at);
    });

    // Take only the first 12 months (like Flutter app limit(12))
    const first12Months = sortedData.slice(0, 12);

    // Reverse the data (like Flutter app: yearlyGraphList.reversed.toList())
    const last12Months = first12Months.reverse();

    // Debug: Log the processed data
    console.log("Processed 12 months:", last12Months);
    console.log("October data:", last12Months.find(item => item.month === 10));

    if (last12Months.length === 0) {
      return null;
    }

    // Generate labels (month names) for the last 12 months
    const labels = last12Months.map(item => {
      const monthIndex = item.month - 1;
      return new Date(2024, monthIndex).toLocaleString('default', { month: 'long' });
    });

    // Use the 'point' field for chart data (cumulative values to show upward trend)
    const monthlyData = last12Months.map((item, index) => {
      const value = parseFloat(item.point);
      const result = isNaN(value) ? 0 : value;
      
      // Debug: Log each month's data
      if (index < 3) { // Log first 3 months for debugging
        console.log(`Month ${item.month} (${labels[index]}): point=${item.point}, amount=${item.amount}, result=${result}`);
      }
      
      return result;
    });

    // Debug: Log the monthly data array
    console.log("Monthly data array:", monthlyData);

    // Calculate the 12-month total performance using 'amount' field (like Flutter app)
    const twelveMonthTotal = last12Months.reduce((sum, item) => {
      const value = parseFloat(item.amount);
      return sum + (isNaN(value) ? 0 : value);
    }, 0);

    // Debug: Log the total calculation
    console.log("Twelve month total:", twelveMonthTotal);

    const data = {
      labels: labels,
      datasets: [
        {
          label: `12-Month Total: ${twelveMonthTotal.toFixed(2)}%`,
          data: monthlyData,
          borderColor: '#00AD3C',
          backgroundColor: 'rgba(0, 173, 60, 0.2)',
          tension: 0.3,
          fill: true,
          pointStyle: 'circle',
          pointRadius: 5,
          pointHoverRadius: 10,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: {
            font: {
              size: 14,
              weight: 600
            },
          },
        },
        tooltip: {
          callbacks: {
            label: function (tooltipItem) {
              const monthName = labels[tooltipItem.dataIndex];
              const monthlyItem = last12Months[tooltipItem.dataIndex];
              const individualAmount = parseFloat(monthlyItem.amount) || 0;
              return `${monthName}: ${individualAmount.toFixed(2)}%`;
            },
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Months (Last 12)',
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Monthly Performance %',
          },
          ticks: {
            callback: function (value) {
              return `${value.toFixed(2)}%`;
            },
          },
        },
      },
    };

    return { data, options };
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;

    try {
      const updateData = {};
      if (editingItem.amount !== undefined && editingItem.amount !== null && editingItem.amount !== '') {
        updateData.amount = parseFloat(editingItem.amount) || 0;
      }
      if (editingItem.point !== undefined && editingItem.point !== null && editingItem.point !== '') {
        updateData.point = parseFloat(editingItem.point) || 0;
      }
      // NO individual_percentage - only use amount field (same as Flutter app)

      // Add updated timestamp
      updateData.updated_at = Timestamp.now();

      await updateDoc(doc(DATABASE, "yearly_graph", editingItem.id), updateData);

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Yearly graph entry updated successfully",
      });

      setEditDialogOpen(false);
      setEditingItem(null);
      loadYearlyData();
    } catch (error) {
      console.error("Error updating item:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update yearly graph entry",
      });
    }
  };

  const getStatusChip = (item) => {
    // Check if this item is part of a duplicate group
    const isDuplicate = duplicates.some(group => 
      group.some(dupItem => dupItem.id === item.id)
    );

    if (isDuplicate) {
      return <Chip label="Duplicate" color="warning" size="small" />;
    }

    // Check for data integrity issues
    // Use ONLY amount field (same as Flutter app)
    if (!item.amount && !item.point) {
      return <Chip label="Missing Data" color="error" size="small" />;
    }

    return <Chip label="Valid" color="success" size="small" />;
  };

  const getDuplicateGroup = (item) => {
    return duplicates.find(group => 
      group.some(dupItem => dupItem.id === item.id)
    );
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Yearly Graph Data Analysis
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Analyze and manage yearly graph data, identify duplicates, and clean up inconsistent entries.
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {yearlyData.length}
              </Typography>
              <Typography variant="body2">Total Entries</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                {duplicates.length}
              </Typography>
              <Typography variant="body2">Duplicate Groups</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error.main">
                {yearlyData.filter(item => !item.amount && !item.point).length}
              </Typography>
              <Typography variant="body2">Missing Data</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {yearlyData.filter(item => item.amount || item.point).length}
              </Typography>
              <Typography variant="body2">Valid Entries</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Actions */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          label="Filter by Email"
          value={filterEmail}
          onChange={(e) => setFilterEmail(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        />
        <TextField
          label="Filter by Month"
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        />
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadYearlyData}
          disabled={loading}
        >
          Refresh
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDeleteSelected}
          disabled={selectedItems.length === 0}
        >
          Delete Selected ({selectedItems.length})
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<PreviewIcon />}
          onClick={handlePreviewYearlyGraph}
        >
          Preview Graph
        </Button>
      </Box>

      {/* Duplicates Alert */}
      {duplicates.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Duplicate Entries Detected
          </Typography>
          <Typography variant="body2">
            Found {duplicates.length} groups of duplicate entries. Review and clean up duplicates to ensure data integrity.
          </Typography>
        </Alert>
      )}

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedItems.length > 0 && selectedItems.length < filteredData.length}
                  checked={filteredData.length > 0 && selectedItems.length === filteredData.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>User Email</TableCell>
              <TableCell>Month</TableCell>
              <TableCell>Year</TableCell>
              {/* NO Individual % column - only use amount field (same as Flutter app) */}
              <TableCell>Point</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Last Updated</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No data found
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((item) => {
                const duplicateGroup = getDuplicateGroup(item);
                return (
                  <TableRow
                    key={item.id}
                    sx={{
                      backgroundColor: duplicateGroup ? "rgba(255, 152, 0, 0.1)" : "inherit",
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedItems.includes(item.id)}
                        onChange={() => handleSelectItem(item.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {item.userID}
                      </Typography>
                      {duplicateGroup && (
                        <Chip
                          label={`${duplicateGroup.length} duplicates`}
                          color="warning"
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{item.month}</TableCell>
                    <TableCell>{item.year}</TableCell>
                    {/* NO Individual % cell - only use amount field (same as Flutter app) */}
                    <TableCell>
                      {item.point !== undefined 
                        ? `${item.point.toFixed(2)}%`
                        : "N/A"
                      }
                    </TableCell>
                    <TableCell>
                      {item.amount !== undefined 
                        ? `${item.amount.toFixed(2)}%`
                        : "N/A"
                      }
                    </TableCell>
                    <TableCell>
                      {dayjs(item.created_at).format("DD/MM/YYYY HH:mm")}
                    </TableCell>
                    <TableCell>
                      {item.updated_at ? dayjs(item.updated_at.toDate()).format("DD/MM/YYYY HH:mm") : "Never"}
                    </TableCell>
                    <TableCell>{getStatusChip(item)}</TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditItem(item)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setItemsToDelete([item.id]);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {itemsToDelete.length} selected item(s)?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Yearly Graph Entry</DialogTitle>
        <DialogContent>
          {editingItem && (
            <Box sx={{ mt: 2 }}>
              {/* NO individual_percentage field - only use amount (same as Flutter app) */}
              <TextField
                fullWidth
                label="Point (Cumulative)"
                type="number"
                value={editingItem.point || ''}
                onChange={(e) => setEditingItem({
                  ...editingItem,
                  point: parseFloat(e.target.value) || 0
                })}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={editingItem.amount || ''}
                onChange={(e) => setEditingItem({
                  ...editingItem,
                  amount: parseFloat(e.target.value) || 0
                })}
                margin="normal"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Yearly Graph Preview</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This preview shows exactly what users see in the app - the cumulative performance trend over the last 12 months. 
            The graph line shows cumulative values to create an upward trend, while tooltips display individual monthly closure amounts.
          </Typography>
          <Box sx={{ height: 400, border: '1px solid #ccc', borderRadius: 1, p: 2 }}>
            {(() => {
              const chartData = generateChartData();
              if (chartData) {
                return (
                  <Box sx={{ width: '100%', height: '350px' }}>
                    <Line data={chartData.data} options={chartData.options} />
                  </Box>
                );
              } else {
                return (
                  <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No yearly graph data available to display
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Please ensure yearly graph data is properly loaded and contains valid performance data
                    </Typography>
                  </Box>
                );
              }
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default YearlyGraphAnalysis;
